
import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { Mail, Send, Activity, User, CheckCircle, XCircle } from 'lucide-react';

const EmailMarketing: React.FC = () => {
  const { emailLogs, emailTemplates, sendEmail, deleteEmailLog } = useData();
  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);

  const handleTemplateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tmplId = e.target.value;
    setSelectedTemplate(tmplId);
    if (tmplId) {
      const tmpl = emailTemplates.find(t => t.id === tmplId);
      if (tmpl) {
        setSubject(tmpl.subject);
        setMessage(tmpl.body);
      }
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    await sendEmail(recipient, subject, message, selectedTemplate || undefined);
    setSending(false);
    setRecipient('');
    setSubject('');
    setMessage('');
    setSelectedTemplate('');
    alert('Email process completed.');
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Email Marketing</h1>
        <p className="text-gray-500">Run campaigns and track delivery status.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Campaign Builder */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100">
           <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2"><Send size={20}/> Quick Send</h3>
           <form onSubmit={handleSend} className="space-y-4">
             <div>
               <label className="block text-sm font-bold text-gray-700 mb-1">Load Template (Optional)</label>
               <select value={selectedTemplate} onChange={handleTemplateSelect} className="w-full border p-2 rounded-sm bg-gray-50">
                 <option value="">-- Select Template --</option>
                 {emailTemplates.map(t => (
                   <option key={t.id} value={t.id}>{t.name}</option>
                 ))}
               </select>
             </div>
             <div>
               <label className="block text-sm font-bold text-gray-700 mb-1">Recipient Email</label>
               <input required type="email" value={recipient} onChange={e => setRecipient(e.target.value)} className="w-full border p-2 rounded-sm" placeholder="client@example.com" />
             </div>
             <div>
               <label className="block text-sm font-bold text-gray-700 mb-1">Subject Line</label>
               <input required type="text" value={subject} onChange={e => setSubject(e.target.value)} className="w-full border p-2 rounded-sm" />
             </div>
             <div>
               <label className="block text-sm font-bold text-gray-700 mb-1">Message Body (HTML)</label>
               <textarea required rows={8} value={message} onChange={e => setMessage(e.target.value)} className="w-full border p-2 rounded-sm font-mono text-sm"></textarea>
             </div>
             <button disabled={sending} type="submit" className="w-full bg-neutral-900 text-white py-3 rounded-sm font-bold hover:bg-black transition-colors disabled:opacity-50">
               {sending ? 'Sending...' : 'Send Email'}
             </button>
           </form>
        </div>

        {/* Analytics & Logs */}
        <div className="space-y-8">
          
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
             <div className="bg-blue-50 p-4 rounded-sm border border-blue-100">
               <div className="text-blue-800 text-sm font-bold uppercase">Total Sent</div>
               <div className="text-3xl font-bold text-blue-900">{emailLogs.length}</div>
             </div>
             <div className="bg-green-50 p-4 rounded-sm border border-green-100">
               <div className="text-green-800 text-sm font-bold uppercase">Delivered</div>
               <div className="text-3xl font-bold text-green-900">{emailLogs.filter(l => l.status === 'Sent' || l.status === 'Opened').length}</div>
             </div>
          </div>

          {/* Logs */}
          <div className="bg-white rounded-sm shadow-sm border border-gray-100 overflow-hidden">
             <div className="px-6 py-4 border-b border-gray-100 font-bold text-gray-900 flex items-center gap-2">
               <Activity size={18} /> Recent Activity Log
             </div>
             <div className="max-h-[500px] overflow-y-auto">
               <table className="w-full text-left text-sm">
                 <thead className="bg-gray-50 text-gray-500">
                   <tr>
                     <th className="p-3">Status</th>
                     <th className="p-3">Recipient</th>
                     <th className="p-3">Time</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-100">
                   {emailLogs.slice(0, 20).map(log => (
                     <tr key={log.id}>
                       <td className="p-3">
                         {log.status === 'Sent' ? (
                           <span className="flex items-center text-green-600 gap-1"><CheckCircle size={14}/> Sent</span>
                         ) : (
                           <span className="flex items-center text-red-600 gap-1"><XCircle size={14}/> Failed</span>
                         )}
                       </td>
                       <td className="p-3 text-gray-900 font-medium">{log.recipient}</td>
                       <td className="p-3 text-gray-500 text-xs">{new Date(log.sentAt).toLocaleTimeString()}</td>
                     </tr>
                   ))}
                 </tbody>
               </table>
               {emailLogs.length === 0 && <div className="p-4 text-center text-gray-500">No emails sent yet.</div>}
             </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default EmailMarketing;
